// HTTP mechanics for the survey API: one GET, one POST, each returning a discriminated result
// instead of throwing, so the caller is forced to handle failure before touching data. What a
// given status code *means* for a survey is not here — that belongs to api.ts. This file only
// knows how to reach the network and how to classify what came back.
//
// globalThis.fetch is the seam: tests stub it and drive every path below through the real code.

export type ApiErrorType = 'network' | 'application' | 'cancelled' | 'unknown'

export interface ApiErrorDetails {
  url: string
  status?: number
  cause?: unknown
}

export class ApiError extends Error {
  readonly type: ApiErrorType
  readonly url: string
  readonly status?: number
  override readonly cause?: unknown

  constructor(type: ApiErrorType, message: string, details: ApiErrorDetails) {
    super(message)
    this.name = 'ApiError'
    this.type = type
    this.url = details.url
    if (details.status !== undefined)
      this.status = details.status
    if (details.cause !== undefined)
      this.cause = details.cause
  }
}

export type ApiResult<T>
  = | { readonly ok: true, readonly data: T }
    | { readonly ok: false, readonly error: ApiError }

export interface GetOptions {
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
}

const TIMEOUT_MS = 30_000

function ok<T>(data: T): ApiResult<T> {
  return { ok: true, data }
}

function err<T = never>(error: ApiError): ApiResult<T> {
  return { ok: false, error }
}

// GET {baseURL}{path}?{query} — signal lets the caller cancel (the element aborts in-flight
// loads on disconnect); cancellation is reported as 'cancelled', never as a network failure.
export async function httpGet<T = unknown>(baseURL: string, path: string, options: GetOptions = {}): Promise<ApiResult<T>> {
  return request<T>('GET', baseURL, path, options, undefined)
}

// POST {baseURL}{path} with a JSON body. No signal: a submission that already left the browser
// should not be cancelled by the widget collapsing behind it.
export async function httpPost<T = unknown>(baseURL: string, path: string, body: unknown): Promise<ApiResult<T>> {
  return request<T>('POST', baseURL, path, {}, body)
}

async function request<T>(
  method: 'GET' | 'POST',
  baseURL: string,
  path: string,
  options: GetOptions,
  body: unknown,
): Promise<ApiResult<T>> {
  // buildUrl uses new URL() which throws on an invalid baseURL — that is a configuration fault,
  // not a network one.
  let url: string
  try {
    url = buildUrl(baseURL, path, options.query)
  }
  catch (cause) {
    return err<T>(new ApiError('unknown', 'Failed to build URL', { url: baseURL + path, cause }))
  }

  const timeout = withTimeout(options.signal, TIMEOUT_MS)

  // Kept out of the fetch try/catch below: a TypeError here is a programmer fault (circular body,
  // invalid header) and toApiError would misfile it as 'network'.
  let req: Request
  try {
    req = new Request(url, {
      method,
      headers: body === undefined
        ? { Accept: 'application/json' }
        : { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      signal: timeout.signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
  }
  catch (cause) {
    timeout.cleanup()
    return err<T>(new ApiError('unknown', 'Failed to build request', { url, cause }))
  }

  try {
    const response = await globalThis.fetch(req)
    return await parseResponse<T>(response, url)
  }
  catch (cause) {
    // A caller-initiated cancel is not a failure to report; anything else is.
    if (timeout.getAbortSource() === 'external')
      return err<T>(new ApiError('cancelled', 'Request was cancelled', { url, cause }))
    return err<T>(toApiError(cause, url))
  }
  finally {
    timeout.cleanup()
  }
}

async function parseResponse<T>(response: Response, url: string): Promise<ApiResult<T>> {
  if (!response.ok) {
    // 錯誤契約是 status-code-only（對齊 server spec：4xx/5xx 無 body）。statusText 在 Node / undici
    // 是空字串，瀏覽器走 HTTP/2 時同樣是空的（HTTP/2 拿掉了 reason phrase），那時只報狀態碼。
    // 機器讀的部分是 error.status，這句話只給人看——狀態碼代表什麼由 api.ts 翻譯，
    // 使用者文案再由 errors.ts 依翻譯出的語意分派，都不讀這個字串。
    const label = response.statusText ? `HTTP ${response.status} ${response.statusText}` : `HTTP ${response.status}`
    return err<T>(new ApiError('application', label, { url, status: response.status }))
  }

  // Read as text so an absent Content-Length can't hide an empty body.
  const text = await response.text()

  // Empty body (204, or any 2xx with nothing in it) is a valid answer, not a parse failure.
  if (text === '')
    return ok(undefined as T)

  try {
    return ok(JSON.parse(text) as T)
  }
  catch (cause) {
    // fetch succeeded and bytes arrived — they are just wrong, so this is application, not network.
    return err<T>(new ApiError('application', 'Invalid JSON in response body', { url, status: response.status, cause }))
  }
}

// DOMException is admitted alongside Error on purpose: jsdom's DOMException is not an instance of
// that realm's Error, so gating on Error alone sends every abort reason to 'unknown' under test
// while a browser takes the 'network' branch — a divergence no test could then catch. The gate
// stays a constructor check rather than a duck-typed `name` read, so an arbitrary thrown object
// is still classified 'unknown' exactly as before.
function toApiError(cause: unknown, url: string): ApiError {
  if (cause instanceof Error || cause instanceof DOMException) {
    const { name } = cause
    // TimeoutError = this module's own timeout; AbortError = an abort that reached us from
    // somewhere other than the caller's signal (a caller cancel is answered before this point);
    // TypeError = fetch itself failed.
    if (name === 'AbortError' || name === 'TimeoutError' || cause instanceof TypeError)
      return new ApiError('network', cause.message, { url, cause })
    return new ApiError('unknown', cause.message, { url, cause })
  }
  return new ApiError('unknown', String(cause), { url, cause })
}

function buildUrl(
  baseURL: string,
  path: string,
  query?: Record<string, string | number | boolean | undefined>,
): string {
  // new URL(relPath, base) keeps the base's own subpath only when base ends with '/' and the
  // relative part does not start with one — without this, new URL('/v1/x', 'https://api.tvbs/pulse')
  // silently drops '/pulse'.
  const base = baseURL.endsWith('/') ? baseURL : `${baseURL}/`
  const relPath = path.startsWith('/') ? path.slice(1) : path

  const url = new URL(relPath, base)

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined)
        url.searchParams.set(key, String(value))
    }
  }

  return url.toString()
}

type AbortSource = 'timeout' | 'external' | null

/**
 * Builds an AbortSignal that fires after timeoutMs OR when the caller's external signal aborts,
 * whichever comes first.
 *
 * Hand-rolled from AbortController + setTimeout rather than AbortSignal.timeout() /
 * AbortSignal.any(): the widget baseline includes iOS Safari 16, which has neither, and
 * Vite's es2022 target does not polyfill Web APIs.
 *
 * cleanup() MUST run once fetch settles, success or failure. It clears the pending timer and
 * detaches the external listener — { once: true } only fires on abort, so on the normal path the
 * listener would otherwise stay attached to a long-lived shared signal for as long as that signal
 * exists.
 *
 * getAbortSource() is how the caller tells a deliberate cancel from a timeout or network fault.
 */
function withTimeout(
  externalSignal: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal, cleanup: () => void, getAbortSource: () => AbortSource } {
  const controller = new AbortController()
  let abortSource: AbortSource = null

  const timeoutId = setTimeout(() => {
    // First-wins: whoever aborted first owns the classification, so a later source must not
    // overwrite it. abort() is idempotent, but the recorded source is what the caller reads.
    if (abortSource !== null)
      return
    abortSource = 'timeout'
    // Name it TimeoutError to match the platform AbortSignal.timeout() spec, which keeps
    // toApiError's TimeoutError branch classifying this as 'network'.
    controller.abort(new DOMException('Request timed out', 'TimeoutError'))
  }, timeoutMs)

  let removeExternalListener: (() => void) | undefined

  if (externalSignal) {
    if (externalSignal.aborted) {
      // Already aborted before we started: abortSource is necessarily still null here.
      abortSource = 'external'
      controller.abort(new DOMException('Request aborted', 'AbortError'))
    }
    else {
      const onExternalAbort = () => {
        if (abortSource !== null)
          return
        abortSource = 'external'
        // Normalize the reason: a caller may abort with a plain string, which must not leak into
        // our signal where it would be classified by name.
        controller.abort(new DOMException('Request aborted', 'AbortError'))
      }
      externalSignal.addEventListener('abort', onExternalAbort, { once: true })
      removeExternalListener = () => externalSignal.removeEventListener('abort', onExternalAbort)
    }
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId)
      removeExternalListener?.()
    },
    getAbortSource: () => abortSource,
  }
}
